import React, { Component } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router";
import { OverviewService, JobService, InvoiceService } from "@services";
import utils from "@utils";
import { VIEW_INVOICE_STATUS } from "@constants";
import { Loader, Tooltip } from "../../components";
import "./Dashboard.scss";

class Dashboard extends Component {
	constructor(props) {
		super(props);
		this.state = {
			overviewData: {},
			jobList: [],
			invoiceList: [],
			overviewLoading: false,
			invoiceLoading: false,
			jobsLoading: false,
		};
	}

	componentDidMount() {
		const { user } = this.props;
		this.getUnstaffedJobs();
		if (
			(user.IsStaffAdmin || user.IsInternalCoordinator) &&
			(user.isCoordinator() || user.isLEA())
		)
			this.getInvoices();
		if ((user.isCoordinator() && user.IsRKPay) || (user.isLEA() && user.IsStaffAdmin))
			this.getOverviewData(user);
	}

	/** Gets overview data used for displaying the counts/totals in the dashboard grid component */
	getOverviewData = (user) => {
		this.setState({ overviewLoading: true });
		OverviewService.getOverviewData({ dateRange: 90 }, user)
			.then(({ data }) => {
				this.setState({ overviewData: data, overviewLoading: false });
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: "Unable to get overview data",
					timeOut: 1500,
				});
			});
	};

	/** Gets a list of invoices to be displayed in the Invoices Pending Approval table */
	getInvoices = () => {
		this.setState({ invoiceLoading: true });

		InvoiceService.getInvoiceList({
			pageNumber: 1,
			sortBy: "Date",
			asc: true,
			displayCount: 5,
			status: VIEW_INVOICE_STATUS.review.value,
		})
			.then(({ data }) => {
				this.setState({ invoiceList: data.ItemList, invoiceLoading: false });
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: "Unable to get overview data",
					timeOut: 1500,
				});
			});
	};

	/** Gets a list of unstaffed jobs to be displayed in the Unstaffed Jobs table */
	getUnstaffedJobs = () => {
		this.setState({ jobsLoading: true });

		const params = {
			status: 2, // Load upcoming jobs
			unStaffed: true, // only unstaffed
			dateRange: 7,
			pageNumber: 1,
			displayCount: 5,
			sortBy: "Date",
			asc: true,
		};

		JobService.getAll(params)
			.then(({ data }) => {
				this.setState({ jobList: data.ItemList, jobsLoading: false });
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: "Unable to get overview data",
					timeOut: 1500,
				});
			});
	};

	render() {
		const { lang } = global;
		const { overviewData, jobList, invoiceList, overviewLoading, invoiceLoading, jobsLoading } =
			this.state;

		const {
			user,
			user: {
				Organization: { ExtraDutyApprovalRequired, JobLocationApprovalRequired },
				TimeZoneId,
			},
		} = this.props;

		// Correct offsets when certain columns are not rendered
		const gridOffset =
			4 - (Number(ExtraDutyApprovalRequired) + Number(JobLocationApprovalRequired)) * 2;

		return (
			<div
				className="container"
				id="dashboard-container"
			>
				{/* Title */}
				<div
					className="row"
					id="title-row"
				>
					<div className="col-sm-3">
						<h1>{lang.t("Dashboard")}</h1>
					</div>
					<If condition={overviewData.DisputesCount > 0}>
						<div
							role="alert"
							className="col-xs-10 col-xs-offset-1 col-sm-6 col-sm-offset-0 alert alert-danger"
						>
							<div className="row">
								<div className="col-xs-12 col-sm-7 message">
									{lang.t("dynamic.You have dispute pending response", {
										disputesCount: overviewData.DisputesCount,
									})}
								</div>
								<div className="col-xs-6 col-sm-2">
									{utils.currencyFormatter(overviewData.DisputeAmount)}
								</div>
								<div className="col-xs-6 text-right col-sm-3">
									<Link
										to="/payments/list/dispute"
										className="btn btn-primary btn-sm"
									>
										{lang.t("Review Now")}
									</Link>
								</div>
							</div>
						</div>
					</If>
				</div>
				{/* Title */}

				<If condition={user.IsStaffAdmin || user.isCoordinator()}>
					{/* Grid */}
					<div
						className="row"
						id="dashboard-grid"
						test-data-id="dashboard__header-cards"
					>
						<Choose>
							{/* LEA Grid */}
							<When condition={user.isLEA()}>
								<div
									className={`col-xs-4 text-center grid-container col-xs-offset-${gridOffset}`}
								>
									<Link
										to="/officers?vettedStatus=1"
										data-test-id="dashboard__officer-pending-verification"
									>
										<h4>{lang.t("Officers Pending Verification")}</h4>
										<h2>{overviewData.OfficersPendingVerificationCount}</h2>
									</Link>
									<Loader
										active={overviewLoading}
										size="small"
									/>
								</div>
								<If condition={ExtraDutyApprovalRequired}>
									<div className="col-xs-4 text-center grid-container">
										<Link
											to="/admin/approvals/list/pending"
											data-test-id="dashboard__jobs-pending-approval"
										>
											<h4>
												{lang.t("Jobs Pending Approval")}
												<span className="visible-sm">
													<br />
												</span>
											</h4>
											<h2>{overviewData.JobsPendingApprovalCount}</h2>
										</Link>
										<Loader
											active={overviewLoading}
											size="small"
										/>
									</div>
								</If>
								<If condition={JobLocationApprovalRequired}>
									<div className="col-xs-4 text-center grid-container">
										<Link
											to="/admin/location/list?status=30"
											data-test-id="dashboard__locations-pending-approval"
										>
											<h4>{lang.t("Locations Pending Approval")}</h4>
											<h2>{overviewData.LocationsPendingApprovalCount}</h2>
										</Link>
										<Loader
											active={overviewLoading}
											size="small"
										/>
									</div>
								</If>
								{/* LEA Grid */}
							</When>
							<Otherwise>
								<Choose>
									<When condition={!user.IsRKPay}>
										{/* Sign Up for RKPAY Grid */}
										<div className="col-sm-6 col-sm-offset-3 text-center">
											<div className="rkpay-button">
												<Link
													to="/my-account#RKPay"
													className="btn btn-primary"
												>
													{lang.t("Sign-Up For RKPay")}
												</Link>
											</div>
										</div>
										{/* Sign Up for RKPAY Grid */}
									</When>

									<Otherwise>
										{/* Non-LEA Grid */}
										<div className="col-xs-6 col-sm-4 col-sm-offset-2 text-center">
											<div className="grid-item">
												<div>
													<h4>{lang.t("Next Payout")}</h4>
													<small
														className={`text-muted${overviewData.NextPayOutDate ? "" : " invisible"}`}
													>
														<Choose>
															<When
																condition={
																	overviewData.NextPayOutDate
																}
															>
																{utils.formatDate(
																	new Date(
																		`${overviewData.NextPayOutDate} 00:00`,
																	),
																	"en-US",
																)}
															</When>
															<Otherwise>&nbsp;</Otherwise>
														</Choose>
													</small>
												</div>
												<div>
													<h3>
														{utils.currencyFormatter(
															overviewData.NextPayOutAmount || 0,
														)}
													</h3>
													<small className="text-muted">
														{`${lang.t("You have made")} `}
														{`${utils.currencyFormatter(overviewData.EarningsToday || 0)} ${lang.t("Today")}`}
													</small>
												</div>
											</div>
										</div>
										<div className="col-xs-6 col-sm-4 text-center">
											<div className="grid-item">
												<div>
													<h4>{lang.t("Coordination Fees")}</h4>
													<small className="text-muted">
														{lang.t("Coordinator charges to client")}
													</small>
												</div>
												<div>
													<h3>
														{utils.currencyFormatter(
															overviewData.Fees || 0,
														)}
													</h3>
													<small className="text-muted">{`90 ${lang.t("Days")}`}</small>
												</div>
											</div>
										</div>
										{/* Non-LEA Grid */}
									</Otherwise>
								</Choose>
							</Otherwise>
						</Choose>
					</div>
					{/* Grid */}
				</If>

				{/* Table Container */}
				<div
					className="row"
					id="table-container"
				>
					{/* Invoice Table */}
					<If
						condition={
							(user.IsStaffAdmin || user.IsInternalCoordinator) &&
							(user.isCoordinator() || user.isLEA())
						}
					>
						<div className="col-md-6">
							<div
								className="container-fluid"
								id="invoice-table"
							>
								<div className="row dashboard-table-header">
									<div className="col-xs-9 col-md-8">
										<h4>{lang.t("Invoices Pending Approval")}</h4>
									</div>
									<div className="col-xs-3 col-md-4 text-right">
										<Link
											to="/payments/list/review"
											data-test-id="dashboard__view-all-invoices-pending"
										>
											<small>{lang.t("View All")}</small>
										</Link>
									</div>
								</div>
								<div className="row dashboard-table-subheader hidden-xs">
									<div className="col-lg-2 col-md-3 col-sm-2">
										{lang.t("Date")}
									</div>
									<div className="col-lg-4 col-md-3 col-sm-4">
										{lang.t("Client")}
									</div>
									<div className="col-md-3 col-sm-3">{lang.t("Officer")}</div>
									<div className="col-sm-3 text-right">{lang.t("Amount")}</div>
								</div>
								<div className="dashboard-table-items">
									<For
										each="invoice"
										index="index"
										of={invoiceList}
									>
										<Link
											key={`pending-${invoice.Id}`}
											to={`/payments/${invoice.Id}`}
											className="row"
										>
											<div className="col-lg-2 col-md-3 col-sm-2">
												{utils.formatDate(
													`${invoice.StartDateTime}Z`,
													"en-US",
													TimeZoneId,
												)}
											</div>
											<div className="col-lg-4 col-md-3 col-sm-4">
												<strong>{invoice.ClientName}</strong>
											</div>
											<div
												className="col-xs-6 col-sm-3 col-md-3 officer-name"
												title={invoice.OfficerName}
											>
												{invoice.OfficerName}
											</div>
											<div className="col-xs-6 col-sm-3 text-right">
												{utils.currencyFormatter(invoice.TotalInvoice || 0)}
											</div>
										</Link>
									</For>
								</div>
								<Loader active={invoiceLoading} />
							</div>
						</div>
					</If>
					{/* Invoice Table */}

					{/* Unstaffed Jobs Table */}
					<div className="col-md-6">
						<div
							className="container-fluid"
							id="job-table"
						>
							<div className="row dashboard-table-header">
								<div className="col-xs-6">
									<h4>{lang.t("Unstaffed Jobs")}</h4>
								</div>
								<div className="col-xs-4 text-right">
									<Link
										to="/jobs/list/unstaffed"
										data-test-id="dashboard__view-all-unstaffed-jobs"
									>
										<small>{lang.t("View All")}</small>
									</Link>
								</div>
								<div className="col-xs-2 text-right">
									<Tooltip content={lang.t("Add Job")}>
										<Link
											to="/jobs/add"
											data-test-id="dashboard__add-job"
										>
											<span
												className="btn btn-default btn-rounded btn-round-small"
												id="add-jobs"
											>
												<i className="rkf rkf-plus" />
											</span>
										</Link>
									</Tooltip>
								</div>
							</div>
							<div className="row dashboard-table-subheader hidden-xs">
								<div className="col-lg-2 col-md-3 col-sm-2">{lang.t("Date")}</div>
								<div className="col-lg-7 col-md-6 col-sm-7">{lang.t("Client")}</div>
								<div className="col-sm-3 text-center">{lang.t("Assigned")}</div>
							</div>
							<div className="dashboard-table-items">
								<For
									each="job"
									index="index"
									of={jobList}
								>
									<Link
										key={`unstaffed-${job.Id}`}
										to={`/jobs/${job.Id}`}
										className="row"
									>
										<div className="col-lg-2 col-md-3 col-sm-2">
											{utils.formatDate(
												`${job.StartDateTime}Z`,
												"en-US",
												TimeZoneId,
											)}
										</div>
										<div className="col-xs-8 col-lg-7 col-md-6 col-sm-7">
											<strong>{job.ClientName}</strong>
											{job.RequirePrepayment && (
												<span
													className="label label-success"
													title={lang.t(
														"This job has been prepaid, no overtime hours are allowed_",
													)}
												>
													{lang.t("Prepaid")}
												</span>
											)}
										</div>
										<div className="col-xs-4 col-sm-3 text-center">
											{`${job.ApplicantsAssigned} / ${job.ApplicantsNeeded}`}
											<span className="item-description">
												&nbsp;{lang.t("Assigned")}
											</span>
										</div>
									</Link>
								</For>
							</div>
							<Loader active={jobsLoading} />
						</div>
					</div>
					{/* Unstaffed Jobs Table */}
				</div>
				{/* Table Container */}
			</div>
		);
	}
}

Dashboard.propTypes = {
	user: PropTypes.object,
};

Dashboard.defaultProps = {
	user: {},
};

export default Dashboard;
